import { Link } from "react-router-dom";
import { ContactBand } from "../components/ContactBand";
import { DeanBand } from "../components/DeanBand";
import { PageHero } from "../components/PageShell";
import { DEANS, deanPhoto } from "../lib/deans";
import { ACADEMIC_YEAR, SEM_III_V_EVENTS, type CalendarEvent } from "../lib/academicCalendar";
import { EXAM_RESULTS, RESULT_KIND_LABEL, type ExamResult } from "../lib/examResults";
import { EXAM_CONTACTS, EXAM_DEAN_REACH } from "../lib/examOffice";
import { inr, publishedExamFees } from "../lib/fees";
import { ROUTES } from "../lib/routes";

const EXAM_DEAN = DEANS.find((d) => d.role === "Dean Examination");

/** The year each month in SEM_III_V_EVENTS's date range falls in. */
const CALENDAR_MONTH_YEAR: Record<string, string> = {
  Sep: "2026", Oct: "2026", Nov: "2026", Dec: "2026", Jan: "2027", Feb: "2027",
};

/**
 * Groups the real III/V-sem circular into the month rows this page has
 * always shown — same source data and order as /academics/calendar, just
 * re-bucketed by month. An entry with no leading "Mon" (the one relative
 * date, "Before LWD −1 wk") stays in whatever month came right before it.
 */
function byMonth(events: CalendarEvent[]) {
  const groups: Array<{ month: string; year: string; events: CalendarEvent[] }> = [];
  let month = "";
  for (const e of events) {
    const lead = e.date.slice(0, 3);
    if (CALENDAR_MONTH_YEAR[lead]) month = lead;
    const last = groups[groups.length - 1];
    if (last && last.month === month.toUpperCase()) last.events.push(e);
    else groups.push({ month: month.toUpperCase(), year: CALENDAR_MONTH_YEAR[month], events: [e] });
  }
  return groups;
}

const calendar = byMonth(SEM_III_V_EVENTS);

// Called out in the hero's own right-hand column — the table further down
// lists every publication, but a student checking the page cold shouldn't
// have to scan past the calendar for the one they're actually waiting on.
// At most one is marked featured at a time in examResults.ts; no featured
// entry leaves the hero exactly as it was (single column, no aside).
const FEATURED_RESULT = EXAM_RESULTS.find((r) => r.featured);

/** Reuses Notifications.tsx's hero-pinned card styling (navy-800 + gold edge, so it reads as a card against the hero's own navy-900) rather than a new one-off style for the same "one thing called out in the hero aside" shape. */
function HeroFeaturedResult({ result }: { result: ExamResult }) {
  return (
    <div className="notif-hero-pinned">
      <div className="notif-hero-pinned-meta">
        <span className="notif-hero-pinned-date">Just published · {result.session}</span>
      </div>
      <h2 className="notif-hero-pinned-title">
        {result.programme} {result.exam} result
      </h2>
      <a
        className="notif-hero-pinned-doc"
        href={result.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${result.programme} ${result.exam} result, ${result.session} (external site)`}
      >
        View result <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
}

export default function Examinations() {
  return (
    <>
      <PageHero
        variant="hero-data"
        eyebrow="Office of the Controller of Examinations"
        title="Schedules, hall tickets and results."
        lead="BIET is affiliated to Visvesvaraya Technological University (VTU), Belagavi. Examinations follow the VTU autonomous calendar; results and revaluation are handled by the on-campus exam cell."
        crumbs={[{ label: "Academics", to: ROUTES.academics }, { label: "Examinations" }]}
        image={null}
        aside={FEATURED_RESULT ? <HeroFeaturedResult result={FEATURED_RESULT} /> : undefined}
      />

      <section className="section exam-band-alt">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Calendar · III &amp; V Sem, AY {ACADEMIC_YEAR}</div>
              <h2 style={{ fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600, marginTop: 8 }}>Important dates</h2>
            </div>
            <Link className="btn-link" to={ROUTES.academicCalendar}>
              Full academic calendar <span className="arr">→</span>
            </Link>
          </div>
          <div className="exam-cal">
            {calendar.map((c, i) => (
              <div key={i} className="exam-cal-row">
                <div className="exam-cal-month">{c.month}<small>{c.year}</small></div>
                <div className="exam-cal-events">
                  {c.events.map((e, j) => (
                    <div key={j} className="exam-cal-evt">
                      <span className="dt">{e.date}</span>
                      <span className="ttl">{e.title}</span>
                      <span className={"tag " + (e.kind === "exam" || e.kind === "result" ? "ongoing" : "")}>{e.kind.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results sit below the calendar: a reader checking dates is mid-term,
          a reader checking results is post-term, and the notices above cover
          whichever is live right now.

          Every release is listed rather than the most recent handful: the six
          newest are all B.E., so a cap hid M.Tech, MCA and MBA completely —
          a postgraduate saw a results table with nothing of theirs in it. */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Results</div>
              <h2 className="exam-h2">Published results</h2>
            </div>
          </div>
          <table className="exam-results">
            <thead>
              <tr>
                <th scope="col">Programme</th>
                <th scope="col">Examination</th>
                <th scope="col">Session</th>
                <th scope="col">View</th>
              </tr>
            </thead>
            <tbody>
              {EXAM_RESULTS.map((r, i) => (
                <tr key={i}>
                  <td className="exam-prog">{r.programme}</td>
                  <td>
                    {r.exam}
                    {r.kind !== "result" && (
                      <span className="exam-kind">{RESULT_KIND_LABEL[r.kind]}</span>
                    )}
                    {r.scheme && <span className="exam-scheme">{r.scheme}</span>}
                  </td>
                  <td className="exam-session">{r.session}</td>
                  <td>
                    <a
                      className="exam-result-link"
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View ${r.programme} ${r.exam} ${RESULT_KIND_LABEL[r.kind].toLowerCase()} result, ${r.session} (external site)`}
                    >
                      View <span aria-hidden="true">↗</span>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Renders only once real figures exist in fees.ts — see EXAM_FEES.
          Certificates & verification hidden by request. */}
      {publishedExamFees().length > 0 && (
        <section className="section exam-band-alt">
          <div className="container">
            <h2 style={{ fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>
              Examination fees
            </h2>
            <table className="exam-fees">
              <thead>
                <tr>
                  <th scope="col">Fee</th>
                  <th scope="col">Charged</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {publishedExamFees().map((f) => (
                  <tr key={f.label}>
                    <td className="exam-fee-label">
                      {f.label}
                      {f.note && <span className="exam-fee-note">{f.note}</span>}
                    </td>
                    <td className="exam-fee-unit">{f.unit ?? "Flat"}</td>
                    <td className="exam-fee-amount">{inr(f.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Carries the dean's own contact details: he previously appeared both as
          a band and as a contact block, under two different spellings. Name and
          role come from deans.ts so there is one source. */}
      {EXAM_DEAN && (
        <DeanBand
          kicker="Office of the Controller of Examinations"
          title="One office sets the timetable and publishes every result."
          name={EXAM_DEAN.name}
          role={EXAM_DEAN.role}
          photo={deanPhoto(EXAM_DEAN)}
          phone={EXAM_DEAN_REACH.phone}
          email={EXAM_DEAN_REACH.email}
        >
          The examination office runs the internal assessment cycle, issues hall
          tickets, conducts the end-semester examinations and publishes results —
          including revaluation and makeup. Queries about a marks card, a
          revaluation window or a certificate start here.
        </DeanBand>
      )}


      {/* The Dean Examination is deliberately absent here: his details sit on
          the band above, so he is named once on the page. */}
      <ContactBand
        title="The examination office."
        entries={EXAM_CONTACTS.map((c) => ({
          role: c.ctype,
          name: c.name,
          phone: c.phone,
          email: c.email,
          extra: c.extra,
        }))}
      />

    </>
  );
}
